import _ from 'lodash';


const ENV_MAX_LEVEL = {
  live: 90,
  test: 90,
  beta: 90,
};

export const MAX_LEVEL = ENV_MAX_LEVEL[process.env.RAIDBOTS_ENV || 'live'];

// https://trinitycore.atlassian.net/wiki/display/tc/item_template
export const SLOT_MAP = {
  1: 'head',
  2: 'neck',
  3: 'shoulder',
  4: 'shirt',
  5: 'chest',
  6: 'waist',
  7: 'legs',
  8: 'feet',
  9: 'wrist',
  10: 'hands',
  11: 'finger',
  12: 'trinket',
  13: 'main_hand', // weapon
  14: 'off_hand', // shield
  15: 'main_hand',
  16: 'back',
  17: 'main_hand', // two-hand
  // 18 is bags
  19: 'tabard',
  20: 'chest',
  21: 'main_hand',
  22: 'off_hand',
  23: 'off_hand',
  // 24 is ammo
  // 25 is thrown
  26: 'main_hand', // some ranged weapons
  // 27 is quiver
  28: 'off_hand', // relic
};

export const SCALING_MAP = {
  '-1': 'Item',
  '-2': 'Consumable',
  '-3': 'Gem1',
  '-4': 'Gem2',
  '-5': 'Gem3',
  '-6': 'Health',
  '-7': 'Item',
  '-8': 'DamageReplaceStat',
  '-9': 'DamageReplaceStat',
};

export const SOCKET_TYPES = {
  PRISMATIC: 'PRISMATIC',
  DOMINATION: 'DOMINATION',
  PRIMORDIAL: 'PRIMORDIAL',
  TINKER: 'TINKER',
  CITRINE_THUNDER: 'CITRINE_THUNDER',
  CITRINE_SEA: 'CITRINE_SEA',
  CITRINE_WIND: 'CITRINE_WIND',
  FIBER: 'FIBER',
};

export const SOCKET_COLORS = {
  1: 'META',
  2: 'RED',
  4: 'YELLOW',
  6: 'ORANGE',
  7: 'PRISMATIC',
  8: 'BLUE',
  10: 'PURPLE',
  12: 'YELLOW',
  14: 'PRISMATIC',
  19: 'RED_PUNCHCARD',
  20: 'YELLOW_PUNCHCARD',
  21: 'BLUE_PUNCHCARD',
  22: 'DOMINATION',
  23: 'SPHEROID',
  24: 'TINKER',
  25: 'PRIMORDIAL',
  27: 'CITRINE_THUNDER',
  28: 'CITRINE_SEA',
  29: 'CITRINE_WIND',
  30: 'FIBER',
};

export const GEM_TO_SOCKET = {
  // Core prismatic gems
  //0x1: 'META',
  0x2: 'PRISMATIC', // red
  0x4: 'PRISMATIC', // yellow
  0x8: 'PRISMATIC', // blue

  // BfA
  //0x20000: 'RED_PUNCHCARD',
  //0x40000: 'YELLOW_PUNCHCARD',
  //0x80000: 'BLUE_PUNCHCARD',

  // Shadowlands
  //0x100000: 'DOMINATION',
  //0x800000: 'SPHEROID',

  // Dragonflight
  0x1000000: 'TINKER', // engineering
  0x2000000: 'PRIMORDIAL', // 10.0.7

  // The War Within
  0x08000000: 'CITRINE_THUNDER',
  0x10000000: 'CITRINE_SEA',
  0x20000000: 'CITRINE_WIND',

  0x40000000: 'FIBER',
};

export const ALGARI_COLOR_COMBO_MAX = 4;

const EXPANSION_DATA = [{
  id: -3,
  name: 'Event / Trading Post',
  enumName: 'EVENT',
}, {
  id: 0,
  name: 'Vanilla',
  enumName: 'VANILLA',
}, {
  id: 1,
  name: 'The Burning Crusade',
  enumName: 'BURNING_CRUSADE',
}, {
  id: 2,
  name: 'Wrath of the Lich King',
  enumName: 'WRATH',
}, {
  id: 3,
  name: 'Cataclysm',
  enumName: 'CATACLYSM',
}, {
  id: 4,
  name: 'Mists of Pandaria',
  enumName: 'MISTS',
}, {
  id: 5,
  name: 'Warlords of Draenor',
  enumName: 'WARLORDS',
}, {
  id: 6,
  name: 'Legion',
  enumName: 'LEGION',
}, {
  id: 7,
  name: 'Battle for Azeroth',
  enumName: 'BFA',
}, {
  id: 8,
  name: 'Shadowlands',
  enumName: 'SHADOWLANDS',
}, {
  id: 9,
  name: 'Dragonflight',
  enumName: 'DRAGONFLIGHT',
}, {
  id: 10,
  name: 'The War Within',
  enumName: 'WAR_WITHIN',
}, {
  id: 11,
  name: 'Midnight',
  enumName: 'MIDNIGHT',
}, {
  id: 12,
  name: 'The Last Titan',
  enumName: 'LAST_TITAN',
}];

export const EXPANSIONS = new Proxy(
  EXPANSION_DATA,
  {
    // lookups can use expansion ID or enum name
    get(target, prop) {
      return _.find(target, { id: parseInt(prop, 10) })
        || _.find(target, { enumName: prop });
    },
  },
);

const envExpansions = {
  live: EXPANSIONS.MIDNIGHT,
  test: EXPANSIONS.MIDNIGHT,
  beta: EXPANSIONS.MIDNIGHT,
};

const env = process.env.RAIDBOTS_ENV || 'live';

export const CURRENT_EXPANSION = envExpansions[env];

// The WoW client major version for the current expansion. The client major
// has always been the expansion enum id + 1 (Classic 1.x is id 0, ...,
// Midnight 12.x is id 11).
export const CURRENT_EXPANSION_VERSION_MAJOR = CURRENT_EXPANSION.id + 1;

// WoW client ItemContext enum values (see TrinityCore DBCEnums.h). The context of
// an item identifies which content tier minted it — the SimC addon reports it in
// bonus_roll_items entries, and item bonus trees key per-context item levels by it.
// Only the values Raidbots consumes are listed; extend as needed.
export const ITEM_CONTEXT = {
  raidNormal: 3,
  raidLfr: 4, // Raid_Raid_Finder
  raidHeroic: 5,
  raidMythic: 6,
  mythicPlusEndOfRun: 16,
  delvesJackpot: 108, // bonus rolls from delve bountiful chests
};

// Try to have no overlap here since it can get messy
export const FAKE_INSTANCE_IDS = {
  mplus: -1,
  azeriteVendor: -2,
  benthicItems: -3,
  mechagonJunkyard: -14,
  mechagonWorkshop: -15,
  pvpHonorShadowlandsSeason1: -16,
  pvpConquestShadowlandsSeason1: -17,
  korthiaItems: -18,
  legionTimewalking: -19,
  tazaveshStreets: -20,
  tazaveshSoleah: -21,
  creationCatalyst: -22,
  sandwornRelic: -25,
  karazhanLower: -26,
  karazhanUpper: -27,
  season4RaidVendor: -28,
  shadowlandsPvpSeason3: -29,
  pvpHonorDragonflightSeason1: -30,
  pvpConquestDragonflightSeason1: -31,
  expansionDungeons: -32,

  dragonflightBlacksmithing: -33,
  dragonflightEnchanting: -34,
  dragonflightEngineering: -35,
  dragonflightInscription: -36,
  dragonflightJewelcrafting: -37,
  dragonflightLeatherworking: -38,
  dragonflightTailoring: -39,

  dragonflightProfessions333: -41,
  dragonflightProfessions382: -42,

  dragonflightAlchemy: -43,

  boeVaultTrash: -44, // placeholder

  primalStorms: -45,

  catalystDragonflight1: -46,

  pvpHonorDragonflightSeason2: -47,
  pvpConquestDragonflightSeason2: -48,
  boeAberrusTrash: -49,

  // 10.1.5 solo content
  timeRifts: -50,

  // 10.1.7 solo content
  dreamsurges: -51,

  // 10.2 PVP
  pvpHonorDragonflightSeason3: -52,
  pvpConquestDragonflightSeason3: -53,
  boeAmirdrassilTrash: -54,

  dawnFall: -55,
  dawnRise: -56,

  pvpWorldDragonflightSeason3: -57,

  dfSeason4RaidVendor: -58,

  pvpHonorDragonflightSeason4: -59,
  pvpConquestDragonflightSeason4: -60,
  pvpWorldDragonflightSeason4: -61,

  pvpHonorWarWithinSeason1: -62,
  pvpConquestWarWithinSeason1: -63,
  pvpWorldWarWithinSeason1: -64,

  warWithinProfessionsRare: -65,
  warWithinProfessionsEpic: -66,

  boeNerubarTrash: -67,

  delveRaresWarWithinSeason1: -68,
  delveEpicsWarWithinSeason1: -69,

  catalystWarWithinSeason1: -70,
  catalystWarWithinSeason2: -71,

  pvpHonorWarWithinSeason2: -72,
  pvpConquestWarWithinSeason2: -73,
  pvpWorldWarWithinSeason2: -74,

  boeUndermineTrash: -75,

  twwNightfall: -76,

  puzzlingCartelChips: -77,

  boeManaforgeTrash: -78,

  pvpHonorWarWithinSeason3: -79,
  pvpConquestWarWithinSeason3: -80,
  pvpWorldWarWithinSeason3: -81,

  catalystWarWithinSeason3: -82,

  puzzlingCartelChipsWarWithinSeason3: -83,

  pvpHonorMidnightSeason1: -84,
  pvpConquestMidnightSeason1: -85,
  pvpWorldMidnightSeason1: -86,

  catalystMidnightSeason1: -87,

  professionMidnightEpic: -88,
  professionMidnightRare: -89,
  professionMidnightPvp: -90,

  raidMidnightSeason1: -91,

  delveMidnightSeason1: -92,
  preyMidnightSeason1: -93,

  pvpHonorMidnightSeason2: -94,
  pvpConquestMidnightSeason2: -95,
  pvpWorldMidnightSeason2: -96,

  delveMidnightSeason2: -98,
  preyMidnightSeason2: -99,
  catalystMidnightSeason2: -100,

  bonusRollMidnightSeason2: -101,
  raidMidnightSeason2: -102,
};

export const FAKE_ENCOUNTER_IDS = {
  relinquishedShoulders: -2,
  relinquishedHelms: -3,
  relinquishedChests: -4,
  benthicHelm: -5,
  benthicShoulders: -6,
  benthicChest: -7,
  benthicLegs: -8,
  benthicBelt: -9,
  benthicWrists: -10,
  benthicCloak: -11,
  benthicFeet: -12,
  benthicGloves: -13,
  pvpHonorShadowlandsSeason1: -14,
  pvpConquestShadowlandsSeason1: -15,
  boeSanctumTrash: -16,
  boeNathriaTrash: -17,
  korthiaItems: -18,
  legionTimewalking: -19,
  // 20 and 21 are tazavesh split
  creationCatalyst: -23,
  boeSepulcherTrash: -24,
  sandwornRelic: -25,
  karazhanLower: -26,
  karazhanUpper: -27,
  season4RaidVendor: -28,
  shadowlandsPvpSeason3: -29,
  pvpHonorDragonflightSeason1: -30,
  pvpConquestDragonflightSeason1: -31,
  expansionDungeonsNotUsed: -32,

  dragonflightBlacksmithing: -33,
  dragonflightEnchanting: -34,
  dragonflightEngineering: -35,
  dragonflightInscription: -36,
  dragonflightJewelcrafting: -37,
  dragonflightLeatherworking: -38,
  dragonflightTailoring: -39,

  dragonflightProfessions333: -41,
  dragonflightProfessions382: -42,

  dragonflightAlchemy: -43,

  boeVaultTrash: -44,
  primalStorms: -45,

  catalystDragonflight1: -46,

  pvpHonorDragonflightSeason2: -47,
  pvpConquestDragonflightSeason2: -48,
  boeAberrusTrash: -49,

  // 10.1.5 solo content
  timeRifts: -50,

  // 10.1.7 solo content
  dreamsurges: -51,

  // 10.2 PVP
  pvpHonorDragonflightSeason3: -52,
  pvpConquestDragonflightSeason3: -53,
  boeAmirdrassilTrash: -54,

  dawnFall: -55,
  dawnRise: -56,

  pvpWorldDragonflightSeason3: -57,

  dfSeason4RaidVendor: -58,

  pvpHonorDragonflightSeason4: -59,
  pvpConquestDragonflightSeason4: -60,
  pvpWorldDragonflightSeason4: -61,

  pvpHonorWarWithinSeason1: -62,
  pvpConquestWarWithinSeason1: -63,
  pvpWorldWarWithinSeason1: -64,

  warWithinProfessionsRare: -65,
  warWithinProfessionsEpic: -66,

  boeNerubarTrash: -67,

  delveRaresWarWithinSeason1: -68,
  delveEpicsWarWithinSeason1: -69,

  catalystWarWithinSeason1: -70,
  catalystWarWithinSeason2: -71,

  pvpHonorWarWithinSeason2: -72,
  pvpConquestWarWithinSeason2: -73,
  pvpWorldWarWithinSeason2: -74,

  boeUndermineTrash: -75,

  twwNightfall: -76,

  puzzlingCartelChips: -77,

  boeManaforgeTrash: -78,

  pvpHonorWarWithinSeason3: -79,
  pvpConquestWarWithinSeason3: -80,
  pvpWorldWarWithinSeason3: -81,

  catalystWarWithinSeason3: -82,

  puzzlingCartelChipsWarWithinSeason3: -83,

  pvpHonorMidnightSeason1: -84,
  pvpConquestMidnightSeason1: -85,
  pvpWorldMidnightSeason1: -86,

  catalystMidnightSeason1: -87,

  professionMidnightEpic: -88,
  professionMidnightRare: -89,
  professionMidnightPvp: -90,

  raidMidnightSeason1: -91,

  delvesMidnightSeason1: -92,
  preyMidnightSeason1: -93,

  pvpHonorMidnightSeason2: -94,
  pvpConquestMidnightSeason2: -95,
  pvpWorldMidnightSeason2: -96,

  boeVenomousAbyssTrash: -97,

  delveMidnightSeason2: -98,
  preyMidnightSeason2: -99,
  catalystMidnightSeason2: -100,
};

// Droptimizer source groups combine multiple instances into a single selectable
// source tile (e.g. "Season 1 Raids" merges all raid wings into one entry).
//
// - groupId: a FAKE_INSTANCE_IDS value used as the virtual instance ID
// - instanceIds: real instance IDs to merge; order matters — the first instance's
//   INSTANCE_OVERRIDES and INSTANCE_DIFFICULTY_OVERRIDES are inherited by the group
// - Encounters are merged with order offsets (idx * 100) per sub-instance
// - getItems fans out across sub-instances and deduplicates the results
// - generate.js builds a synthetic instance entry from each group
// The Season 2 raid pool: The Venomous Abyss + The Tidebound Grotto world boss.
// Shared by the Season 2 Raids group and the Bonus Rolls group so the two can't
// drift. Order matters — the first instance is the group's override anchor.
const SEASON_2_RAID_INSTANCE_IDS = [1320, 1317];

export const DROPTIMIZER_SOURCE_GROUPS = [{
  groupId: FAKE_INSTANCE_IDS.raidMidnightSeason1,
  name: 'Season 1 Raids',
  instanceIds: [1307, 1314, 1308],
  type: 'raid',
  order: 9,
  image_button: 'ui-ej-dungeonbutton-midnight',
}, {
  groupId: FAKE_INSTANCE_IDS.raidMidnightSeason2,
  name: 'Season 2 Raids',
  instanceIds: SEASON_2_RAID_INSTANCE_IDS,
  type: 'raid',
  order: 9,
  image_button: 'ui-ej-dungeonbutton-midnight',
}, {
  // Bonus Roll planner: the S2 raids + the M+ chest in ONE sim. Unlike same-type
  // groups, each member sims at its own difficulty — the selected 'bonus-roll'
  // tier resolves per member via memberDifficulties (shared/droptimizer.js
  // resolveMemberDifficulty). Premium-only; extensible to delve/prey members.
  groupId: FAKE_INSTANCE_IDS.bonusRollMidnightSeason2,
  name: 'Bonus Rolls',
  instanceIds: [...SEASON_2_RAID_INSTANCE_IDS, FAKE_INSTANCE_IDS.mplus],
  type: 'bonus-roll',
  order: 8,
  image_button: 'ui-ej-dungeonbutton-midnight',
  // trash BOE pseudo-encounters are not real bosses and cannot be bonus
  // rolled — drop them from the merged encounters and the item pool
  excludeTrashEncounters: true,
  // the final-boss omnitoken is not in the roll tables (its expanded pieces
  // are identified by slotItemSequenceOffsets); regular per-slot tier tokens
  // from earlier bosses DO bonus roll and stay in the pool
  excludeOmnitokenItems: true,
}];

export const SEASONS = [{
  id: 37,

  active: true,

  name: 'Midnight Season 2',
  shortName: 'mid2',

  // check JournalTierXInstance — hotfixed from 158721 to 156363 in 12.1.0.69273
  // when the season went live; hotfix rows override the static value wago shows
  journalTierAvailabilityCondition: 156363,
  currentWorldStateExpression: 52104, // check JournalEncounterItem for current one

  // upgrade-track ItemBonusListGroups (verify via bonuses.json `.upgrade.group`/`.currency`)
  //
  // Five tracks, no Explorer: S1 had six groups (607 Explorer through 612 Myth) but S2 ships
  // 614-618, and 613 — the slot an S2 Explorer would occupy, immediately before Adventurer —
  // does not exist as of the final pre-release build (12.1.0.69111). Bonuses 12857-12864 do
  // point at a group 619 via type-34, but their item levels are 323-346, i.e. ABOVE Myth 6/6
  // (334) and equal to S1 Explorer + 78 where S2 tracks are S1 + 46 — so 619 belongs to a
  // later season, not this one. Nothing to add here.
  bonusListGroups: [
    614, // Adventurer (Adventurer Mistcrest 3442)
    615, // Veteran (Veteran Mistcrest 3443)
    616, // Champion (Champion Mistcrest 3444)
    617, // Hero (Hero Mistcrest 3445)
    618, // Myth (Myth Mistcrest 3446)
  ],

  itemConversionId: 13, // check ItemConversion for new IDs
  itemConversionCurrency: 3465, // Venomblight Manaflux

  // Bonus roll currency (Voidcore) — transmuted into gear on raid kills / M+ / delves / prey
  // hunts. generate.js auto-discovers the id by flavor text (Simulationcraft.bonusRollCurrencies
  // in simc-addon.lua). S2 reuses 12.0.7's Nebulous Voidcore: the S2-flavoured 3511 never came
  // out of '[DNT, Unused]' through the final pre-release build (12.1.0.69111), so there is no
  // new currency to point at. The icon (the Great Vault Summary roll rows lead with it) is
  // hand-maintained like addSocketItem — update it alongside the id.
  bonusRollCurrency: {
    id: 3418,
    icon: 'inv_1205_voidforge_fluctuatingvoidcores_green',
  },

  // simc-addon.lua will likely have these IDs and names
  crestCurrencies: [
    3442, // Adventurer Mistcrest
    3443, // Veteran Mistcrest
    3444, // Champion Mistcrest
    3445, // Hero Mistcrest
    3446, // Myth Mistcrest
  ],
  // Achievement name pattern has been like "Veteran of the Dawn" -> S2 is "X of the Mist"
  warbandCrestDiscountAchievements: {
    3442: 62410, // Adventurer of the Mist
    3443: 62411, // Veteran of the Mist
    3444: 62412, // Champion of the Mist
    3445: 62414, // Hero of the Mist
    3446: 62416, // Myth of the Mist
  },
  upgradeItems: [
    274476, // Spark of Tides
  ],
  // GameData.Crafting.slots IDs that hold the seasonal spark reagent
  craftingSparkSlots: [
    401, // Spark
    500, // Spark
  ],
  // 12.0 made it so you can use currency directly to craft, so no alternate currency map is needed
  craftingCurrencyAlternate: {},

  // boostUpgrades: the S2 seasonal item-level boost is NOT live at 12.1 launch and is not finalized
  // in DBC yet, so it is left as a comment for now. S2 theme is "Venomforged" (analog of S1
  // "Voidforged"/Ascendant Voidcore 268552). Per Dave it adds necklaces and caps at Myth 8.
  // Candidate boost item: Insidious Venomstone (279997). Venomforged Relic/Remnant/Icon
  // (270921/270925/270929, ilvl 219) drop from The Venomous Abyss. Fill id/bonusId/itemLevel/
  // slots/upgradeGroup per the S1 boostUpgrades shape once the boost event is datamined/goes live.
  // boostUpgrades: [{
  //   id: 279997,
  //   itemName: 'Insidious Venomstone',
  //   itemIcon: '...',
  //   name: '... Venomforged',
  //   slots: ['main_hand', 'off_hand', 'trinket', 'neck'], // S2 adds necklaces
  //   upgradeGroup: 0, // Hero
  //   bonusId: 0,
  //   itemLevel: 0,
  // }],

  // any slots that have a static legendary that will always be used
  // e.g. Reshii Wraps in 11.2
  // Nothing in 12.1 as far as I know
  staticLegendarySlots: [],

  // used for warnings about slots without enchants
  // carried over from S1 - revisit if 12.1 changes enchantable slots
  dpsEnchantSlots: [
    'main_hand',
    'off_hand',
    'chest',
    'feet',
    'rings',
    'hands',
    'wrist',
    'legs',
    'waist',
  ],

  // carried over from S1 - revisit if 12.1 changes socket slots
  sockets: [{
    slot: 'head',
    vault: 1,
  }, {
    slot: 'wrist',
    vault: 1,
  }, {
    slot: 'waist',
    vault: 1,
  }, {
    slot: 'finger',
    extraSockets: 1,
  }, {
    slot: 'neck',
    extraSockets: 1,
  }],

  craftedSocketSlots: [
    'neck',
    'finger',
  ],

  // The Great Vault "add a socket" option item — hand-maintained per-season
  // pointer (like boostUpgrades). Its presence gates the Top Gear
  // Add Vault Socket button; the report summary uses it for the socket row.
  addSocketItem: {
    id: 263897,
    name: 'Radiant Jewelbinder',
    icon: 'inv_10_misc_winterpeltfurbolg_toy',
  },

  // The Bonus Roll Droptimizer source group for this season (the fake instance
  // that merges the raid + M+ chest). Season-scoped pointer for Top Gear's
  // Great Vault consumers — its presence gates the bonus-roll half of the
  // panel. The group itself is defined in INSTANCE_GROUPS (above) and
  // droptimizer.js INSTANCE_OVERRIDES.
  bonusRollGroupId: FAKE_INSTANCE_IDS.bonusRollMidnightSeason2,

  // Vantus Rune: Tides - https://www.wowhead.com/item=272194
  // spellId is documentary only; simcinput uses `amount` (versatility rating)
  vantusRune: {
    spellId: 1290561,
    amount: 162, // S2 versatility
  },
}, {
  id: 34,

  name: 'Midnight Season 1',
  shortName: 'mid1',

  journalTierAvailabilityCondition: 149388, // check JournalTierXInstance
  currentWorldStateExpression: 50188, // check JournalEncounterItem for current one

  // check bonus-upgrade-sets for current upgrade currency
  bonusListGroups: [
    607, // Explorer
    608, // Adventurer
    609, // Veteran
    610, // Champion
    611, // Hero
    612, // Myth
  ],

  itemConversionId: 12, // check ItemConversion for new IDs
  itemConversionCurrency: 3378, // Dawnlight Manaflux

  // Bonus roll currency (Voidcore) — see mid2 note above. Icon TBD (this season
  // predates the roll-currency icon; add it here if this season goes current).
  bonusRollCurrency: { id: 3418 }, // Nebulous Voidcore

  // simc-addon.lua will likely havae these IDs and names
  crestCurrencies: [
    3383, // Adventurer Dawncrest
    3341, // Veteran Dawncrest
    3343, // Champion Dawncrest
    3345, // Hero Dawncrest
    3347, // Myth Dawncrest
  ],
  // Achievement name pattern has been like "Gilded of the Undermine" or "Harbinger of the Gilded"
  warbandCrestDiscountAchievements: {
    3383: 61809, // Adventurer of the Dawn
    3341: 42767, // Veteran of the Dawn
    3343: 42768, // Champion of the Dawn
    3345: 42769, // Hero of the Dawn
    3347: 42770, // Myth of the Dawn
  },
  upgradeItems: [
    232875, // Spark of Radiance
  ],
  // GameData.Crafting.slots IDs that hold the seasonal spark reagent
  craftingSparkSlots: [
    401, // Spark
    500, // Spark
  ],
  // 12.0 made it so you can use currency directly to craft, so no alternate currency map is needed
  craftingCurrencyAlternate: {},
  boostUpgrades: [{
    id: 268552,
    itemName: 'Ascendant Voidcore',
    itemIcon: 'inv_1205_voidforge_sovereignvoidcores_cosmicvoid',
    name: '285 Voidforged',
    slots: ['main_hand', 'off_hand', 'trinket'],
    upgradeGroup: 611, // Hero
    bonusId: 13653,
    itemLevel: 285,
  }, {
    id: 268552,
    itemName: 'Ascendant Voidcore',
    itemIcon: 'inv_1205_voidforge_sovereignvoidcores_cosmicvoid',
    name: '295 Voidforged',
    slots: ['main_hand', 'off_hand', 'trinket'],
    sparkId: 232875, // Spark of Radiance
    bonusId: 13655,
    itemLevel: 295,
  }, {
    id: 268552,
    itemName: 'Ascendant Voidcore',
    itemIcon: 'inv_1205_voidforge_sovereignvoidcores_cosmicvoid',
    name: '298 Voidforged',
    slots: ['main_hand', 'off_hand', 'trinket'],
    upgradeGroup: 612, // Myth
    bonusId: 13654,
    itemLevel: 298,
  }],

  // any slots that have a static legendary that will always be used
  // e.g. Reshii Wraps in 11.2
  // Nothing in 12.0 as far as I know
  staticLegendarySlots: [],

  // used for warnings about slots without enchants
  // this can change season-to-season (head enchants in 11.1 but not 11.2 because of corruptions stuff)
  dpsEnchantSlots: [
    // TODO: Figure this out for 12.0
    'main_hand',
    'off_hand',
    'chest',
    'feet',
    'rings',
    'hands',
    'wrist',
    'legs',
    'waist',
  ],

  sockets: [{
    slot: 'head',
    vault: 1,
  }, {
    slot: 'wrist',
    vault: 1,
  }, {
    slot: 'waist',
    vault: 1,
  }, {
    slot: 'finger',
    extraSockets: 1,
  }, {
    slot: 'neck',
    extraSockets: 1,
  }],

  craftedSocketSlots: [
    // TODO: Figure this out for 12.0
    'neck',
    'finger',
  ],

  // Vantus Rune: Radiant - https://www.wowhead.com/item=245880
  vantusRune: {
    spellId: 1277389,
    amount: 270,
  },
}, {
  id: 30,

  name: 'The War Within Season 3',
  shortName: 'tww3',

  journalTierAvailabilityCondition: 141987, // check JournalTierXInstance
  currentWorldStateExpression: 48764, // check JournalEncounterItem for current one

  bonusListGroups: [
    513, // Explorer
    514, // Adventurer
    515, // Veteran
    516, // Champion
    517, // Hero
    518, // Myth
  ],

  itemConversionId: 11,
  itemConversionCurrency: 3269, // Ethereal Voidsplinter

  crestCurrencies: [
    3284, // Weathered Ethereal Crest
    3286, // Carved Ethereal Crest
    3288, // Runed Ethereal Crest
    3290, // Gilded Ethereal Crest
  ],
  warbandCrestDiscountAchievements: {
    3284: 41886, // Weathered Ethereal Crest
    3286: 41887, // Carved Ethereal Crest
    3288: 41888, // Runed Ethereal Crest
    3290: 41892, // Gilded Ethereal Crest
  },
  upgradeItems: [
    231756, // Spark of Starlight
    231767, // Enchanted Weathered Ethereal Crest
    231769, // Enchanted Runed Ethereal Crest
    231768, // Enchanted Gilded Ethereal Crest
  ],

  // Maps a crafted/event item ID back to its underlying crest currency. Required for 11.x where
  // crafting consumed an enchanted-crest item rather than crests directly.
  craftingCurrencyAlternate: {
    // TWW Season 3 Turbo Boost

    // Gilded Starlight Matrix
    251993: {
      id: 3290,
      amount: 30,
      type: 'currency',
      name: 'Gilded Ethereal Crest',
      icon: 'inv_crestupgrade_ethereal_gilded_enchanted',
    },

    // Runed Starlight Matrix
    251994: {
      id: 3288,
      amount: 15,
      type: 'currency',
      name: 'Runed Ethereal Crest',
      icon: 'inv_crestupgrade_ethereal_runed_enchanted',
    },

    // TWW Season 3

    // Enchanted Weathered Ethereal Crest
    231767: {
      id: 3284,
      amount: 30,
      type: 'currency',
      name: 'Weathered Ethereal Crest',
      icon: 'inv_crestupgrade_ethereal_weathered_enchanted',
    },
    // Enchanted Runed Ethereal Crest
    231769: {
      id: 3288,
      amount: 45,
      type: 'currency',
      name: 'Runed Ethereal Crest',
      icon: 'inv_crestupgrade_ethereal_runed_enchanted',
    },
    // Enchanted Gilded Ethereal Crest
    231768: {
      id: 3290,
      amount: 60,
      type: 'currency',
      name: 'Gilded Ethereal Crest',
      icon: 'inv_crestupgrade_ethereal_gilded_enchanted',
    },
  },

  // any slots that have a static legendary that will always be used
  // e.g. Reshii Wraps in 11.2
  staticLegendarySlots: ['back'],

  // used for warnings about slots without enchants
  // this can change season-to-season (head enchants in 11.1 but not 11.2 because of corruptions stuff)
  dpsEnchantSlots: [
    'main_hand',
    'off_hand',
    'chest',
    'feet',
    'rings',
    'hands',
    'wrist',
    'legs',
    'waist',
  ],

  craftedSocketSlots: [
    'neck',
    'finger',
  ],
}, {
  id: 25,

  name: 'The War Within Season 2',
  shortName: 'tww2',

  journalTierAvailabilityCondition: 128527, // check JournalTierXInstance

  bonusListGroups: [
    437, // Explorer
    438, // Adventurer
    439, // Veteran
    440, // Champion
    441, // Hero
    442, // Myth
  ],

  itemConversionId: 10,
  itemConversionCurrency: 3116, // Essence of Kaja'mite

  warbandCrestDiscountAchievements: {
    3107: 40942, // Weathered
    3108: 40943, // Carved
    3109: 40944, // Runed
    3110: 40945, // Gilded
  },

  upgradeItems: [
    230906, // Spark of Fortunes
    230937, // Enchanted Weathered Undermine Crest
    230936, // Enchanted Runed Undermine Crest
    230935, // Enchanted Gilded Undermine Crest
  ],

  // For small, extra ilevel bumps from Augment items.
  // Like War Within Season 2 Turbo Boost.
  // https://www.wowhead.com/guide/the-war-within/turbo-boost-dinar-event-overview
  craftingLevelAugmentSlots: [
    384, // Augment Beyond
  ],

  craftingCurrencyAlternate: {
    /*
    // TWW Season 2 Turbo Boost

    // Gilded Augmentation Matrix
    239146: {
      id: 3110,
      amount: 30,
      type: 'currency',
      name: 'Gilded Undermine Crest',
      icon: 'inv_crestupgrade_undermine_gilded',
    },

    // Runed Augmentation Matrix
    239203: {
      id: 3109,
      amount: 30,
      type: 'currency',
      name: 'Runed Undermine Crest',
      icon: 'inv_crestupgrade_undermine_runed',
    },
    */

    // Enchanted Weathered Undermine Crest
    230937: {
      id: 3107,
      amount: 30,
      type: 'currency',
      name: 'Weathered Undermine Crest',
      icon: 'inv_crestupgrade_undermine_weathered',
    },
    // Enchanted Runed Undermine Crest
    230936: {
      id: 3109,
      amount: 45,
      type: 'currency',
      name: 'Runed Undermine Crest',
      icon: 'inv_crestupgrade_undermine_runed',
    },
    // Enchanted Gilded Undermine Crest
    230935: {
      id: 3110,
      amount: 60,
      type: 'currency',
      name: 'Gilded Undermine Crest',
      icon: 'inv_crestupgrade_undermine_gilded',
    },
  },

  // TODO: Figure out if this is 99 or 100
  // milestoneSeasonId: 99,
}, {
  id: 24,

  name: 'The War Within Season 1',
  shortName: 'tww1',

  journalTierAvailabilityCondition: 125989,

  bonusListGroups: [
    329, // Explorer
    330, // Adventurer
    331, // Veteran
    332, // Champion
    333, // Hero
    334, // Myth
  ],

  itemConversionId: 8,
  itemConversionCurrency: 2813, // Harmonized Silk

  warbandCrestDiscountAchievements: {
    2914: 40107, // Weathered
    2915: 40115, // Carved
    2916: 40118, // Runed
    2917: 40939, // Gilded
  },

  craftingSparkSlots: [
    256, // TWW spark slot
  ],

  craftingCurrencyAlternate: {
    // Enchanted Weathered Harbinger Crest
    224069: {
      id: 2914,
      amount: 30,
      type: 'currency',
      name: 'Weathered Harbinger Crest',
      icon: 'inv_crestupgrade_xalatath_weathered',
    },
    // Enchanted Runed Harbinger Crest
    224072: {
      id: 2916,
      amount: 45,
      type: 'currency',
      name: 'Runed Harbinger Crest',
      icon: 'inv_crestupgrade_xalatath_runed',
    },
    // Enchanted Gilded Harbinger Crest
    224073: {
      id: 2917,
      amount: 60, // 11.0.7
      type: 'currency',
      name: 'Gilded Harbinger Crest',
      icon: 'inv_crestupgrade_xalatath_gilded',
    },
  },

  // TODO: Figure out if this is 99 or 100
  // milestoneSeasonId: 99,
}, {
  id: 23,
  milestoneSeasonId: 94,

  name: 'Dragonflight Season 4',
  shortName: 'df4',

  journalTierAvailabilityCondition: 117114,

  itemConversionId: 9,
}, {
  id: 22,
  milestoneSeasonId: 93,

  name: 'Dragonflight Season 3',
  shortName: 'df3',

  itemConversionId: 7,
}, {
  id: 21,
  milestoneSeasonId: 92,

  name: 'Dragonflight Season 2',
  shortName: 'df2',
}, {
  id: 19,
  milestoneSeasonId: 91,

  name: 'Dragonflight Season 1',
  shortName: 'df1',

  craftingSparkSlots: [
    247, // Spark
  ],
}];

export const CURRENT_SEASON = _.find(SEASONS, { active: true });

// Flat currencyId -> achievementId lookup merged across all seasons.
export const WARBAND_CREST_DISCOUNT_ACHIEVEMENT = _.reduce(
  SEASONS,
  (acc, season) => Object.assign(acc, season.warbandCrestDiscountAchievements),
  {},
);
